import {
  useCallback,
  useEffect,
  useState,
} from "react";

const STORAGE_KEY =
  "acta.pendingCall.active";

const RETURN_DELAY_MS = 3000;

function readStoredSession() {
  try {
    const raw =
      window.localStorage.getItem(
        STORAGE_KEY
      );

    return raw
      ? JSON.parse(raw)
      : null;
  } catch {
    return null;
  }
}

function writeStoredSession(
  session
) {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(session)
    );
  } catch {
    // Le backend reste la source de vérité.
  }
}

function removeStoredSession() {
  try {
    window.localStorage.removeItem(
      STORAGE_KEY
    );
  } catch {
    // Stockage local indisponible.
  }
}

function contactSnapshot(
  contact = {}
) {
  const firstName =
    contact.firstName ||
    contact.first_name ||
    "";

  const lastName =
    contact.lastName ||
    contact.last_name ||
    "";

  return {
    id:
      contact.id ||
      contact._id ||
      "",
    firstName,
    lastName,
    company:
      contact.company ||
      contact.companyName ||
      "",
    phone:
      contact.phone ||
      contact.contactPhone ||
      "",
    name:
      contact.name ||
      [firstName, lastName]
        .filter(Boolean)
        .join(" ") ||
      contact.company ||
      "ce contact",
  };
}

export default function usePendingCallSession({
  contactId = "",
  fallbackActivity = null,
  fallbackContact = null,
  forceOpen = false,
} = {}) {
  const [
    pending,
    setPending,
  ] = useState(null);

  const [
    returnOpen,
    setReturnOpen,
  ] = useState(false);

  useEffect(() => {
    const stored =
      readStoredSession();

    const storedContactId =
      stored?.contact?.id ||
      stored?.prospectId ||
      "";

    const matchesContact =
      !contactId ||
      !storedContactId ||
      String(storedContactId) ===
        String(contactId);

    const restored =
      stored?.activity?.id &&
      matchesContact
        ? stored
        : null;

    const fallback =
      fallbackActivity?.id
        ? {
            activity:
              fallbackActivity,
            contact:
              contactSnapshot(
                fallbackContact || {
                  id: contactId,
                }
              ),
            launchedAt: 0,
            resolved: false,
          }
        : null;

    const candidate =
      restored || fallback;

    if (
      !candidate?.activity?.id
    ) {
      return;
    }

    setPending(candidate);

    const oldEnough =
      !candidate.launchedAt ||
      Date.now() -
        Number(
          candidate.launchedAt
        ) >
        RETURN_DELAY_MS;

    if (
      forceOpen ||
      oldEnough
    ) {
      setReturnOpen(true);
    }
  }, [
    contactId,
    fallbackActivity,
    fallbackContact,
    forceOpen,
  ]);

  useEffect(() => {
    const reopen = () => {
      if (
        document.visibilityState ===
          "visible" &&
        pending?.activity?.id &&
        !pending?.resolved
      ) {
        setReturnOpen(true);
      }
    };

    document.addEventListener(
      "visibilitychange",
      reopen
    );

    window.addEventListener(
      "pageshow",
      reopen
    );

    return () => {
      document.removeEventListener(
        "visibilitychange",
        reopen
      );

      window.removeEventListener(
        "pageshow",
        reopen
      );
    };
  }, [
    pending?.activity?.id,
    pending?.resolved,
  ]);

  const remember =
    useCallback(
      (
        activity,
        contact
      ) => {
        if (!activity?.id) {
          return;
        }

        const session = {
          activity,
          contact:
            contactSnapshot(
              contact
            ),
          launchedAt:
            Date.now(),
          resolved: false,
        };

        setPending(session);
        setReturnOpen(false);
        writeStoredSession(
          session
        );
      },
      []
    );

  const closeReturn =
    useCallback(() => {
      setReturnOpen(false);
    }, []);

  const markResolved =
    useCallback(() => {
      removeStoredSession();

      setPending(
        (current) =>
          current
            ? {
                ...current,
                resolved: true,
              }
            : current
      );
    }, []);

  const finish =
    useCallback(() => {
      removeStoredSession();
      setPending(null);
      setReturnOpen(false);
    }, []);

  return {
    pending,
    returnOpen,
    remember,
    closeReturn,
    markResolved,
    finish,
  };
}
